import jobPositionPlaceholder from '@public/assets/job-position-placeholder.jpg';
import { useTranslation } from 'next-i18next/pages';
import Image from 'next/image';
import Link, { LinkProps } from 'next/link';
import { ReactNode } from 'react';

import { SourceDomain } from '@pflegia/types/api/source-domain/source-domain';
import { ReactComponent as ThreeLines } from '@pflegia/ui/icons/three-lines.svg';

import ButtonLink from '../button-link/ButtonLink';
import Button from '../button/Button.component';
import { ContainerSection } from '../container-section/ContainerSection';

interface ExternalHeaderLink {
  children: ReactNode;
  href: LinkProps['href'];
  key: string;
  useJsNavigation?: boolean;
}

interface ExternalHeaderProps {
  links: ExternalHeaderLink[];
  logoSrc?: string | null;
  name: string;
  appName?: string;
}

export const ExternalHeader: React.FC<ExternalHeaderProps> = ({ links, logoSrc, name, appName = SourceDomain.PflegiaDe }) => {
  const { t } = useTranslation('funnel', { keyPrefix: 'externalHeader' });

  return (
    <nav className='z-popover sticky top-0 h-24 bg-white drop-shadow-sm'>
      <ContainerSection className='flex h-full items-center justify-between'>
        <div className='flex items-center gap-x-2'>
          <Image
            alt=''
            className='h-12 w-12 rounded-full border-2 object-contain'
            height={48}
            width={48}
            src={logoSrc || jobPositionPlaceholder}
          />
          <div>
            <h1 className='line-clamp-1 text-lg font-medium'>{name}</h1>
            <p className='text-sm text-gray-500'>{t('applyDirectly', { appName })}</p>
          </div>
        </div>
        {/* tabIndex needed for focus-within in Safari */}
        <div className='group relative' tabIndex={-1}>
          <Button icon={<ThreeLines />} className='px-2.5 text-black sm:hidden' colorScheme='inverted' type='button' />
          <ul className='text-default absolute right-0 hidden w-max flex-col rounded-sm bg-white shadow-xs group-focus-within:flex sm:static sm:flex sm:flex-row sm:shadow-none'>
            {links.map(link => (
              <li key={link.key}>
                {link.useJsNavigation ? (
                  <ButtonLink
                    href={link.href}
                    className='hover:bg-primary-100 sm:text-main-blue block w-full p-4 text-left sm:font-medium sm:hover:bg-transparent sm:hover:underline'>
                    {link.children}
                  </ButtonLink>
                ) : (
                  <Link
                    className='hover:bg-primary-100 sm:text-main-blue block p-4 sm:font-medium sm:hover:bg-transparent sm:hover:underline'
                    href={link.href}>
                    {link.children}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      </ContainerSection>
    </nav>
  );
};
